import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSections, isSectionOn } from "@/hooks/useSections";
import { Linkedin, Calendar, ArrowUpRight } from "lucide-react";

/**
 * Category label pair for an article, taken directly from what admin typed
 * into `category_ar` / `category_en`. Falls back to legacy `tag_*` fields.
 * The `key` is a normalised slug used for the filter selection state.
 */
function articleCategories(a) {
  const raw_ar = (a.category_ar || a.tag_ar || "").split(",").map((s) => s.trim()).filter(Boolean);
  const raw_en = (a.category_en || a.tag_en || "").split(",").map((s) => s.trim()).filter(Boolean);
  const n = Math.max(raw_ar.length, raw_en.length);
  const out = [];
  for (let i = 0; i < n; i++) {
    const ar = raw_ar[i] || raw_en[i] || "";
    const en = raw_en[i] || raw_ar[i] || "";
    const key = (en || ar).toLowerCase().replace(/[^a-z0-9\u0600-\u06FF]+/gi, "_").replace(/^_+|_+$/g, "");
    if (key) out.push({ key, ar, en });
  }
  return out;
}

export default function Articles() {
  const { t, lang } = useI18n();
  const sections = useSections();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  useEffect(() => { api.get("/articles").then((r) => setItems(
    (r.data || []).filter((a) => a.published !== false)
  )); }, []);

  // Build category filter dynamically from the categories actually in use.
  const categories = useMemo(() => {
    const map = new Map();
    for (const a of items) for (const c of articleCategories(a)) if (!map.has(c.key)) map.set(c.key, c);
    return [{ key: "all", ar: "الكل", en: "All" }, ...map.values()];
  }, [items]);

  const filtered = useMemo(
    () => (active === "all"
      ? items
      : items.filter((a) => articleCategories(a).some((c) => c.key === active))),
    [items, active],
  );

  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB",
        { year: "numeric", month: "long", day: "numeric" });
    } catch (_e) { return ""; }
  };

  if (!isSectionOn(sections, "articles")) return (
    <div className="container-x py-24 text-navy/60" data-testid="articles-off">
      {lang === "ar" ? "هذا القسم غير متاح حالياً." : "This section is currently unavailable."}
    </div>
  );

  return (
    <div data-testid="articles-page">
      {/* Slim hero — editorial */}
      <section className="bg-navy text-cream py-16">
        <div className="container-x">
          <span className="eyebrow">{t("nav.articles")}</span>
          <h1 className="mt-4 text-3xl md:text-5xl">
            {lang === "ar" ? "المكتبة" : "Library"}
          </h1>
          <span className="divider-gold" />
          <p className="max-w-2xl text-cream/85 text-base leading-relaxed">
            {lang === "ar"
              ? "رؤى وتحليلات مختارة من فريق HAWKAM حول الحوكمة وحماية البيانات و GDPR والذكاء الاصطناعي والصحة الرقمية."
              : "Curated insights from the HAWKAM team on governance, data protection, GDPR, AI, GRC and digital health."}
          </p>
        </div>
      </section>

      <section className="container-x py-12">
        {/* Category filter — dynamic from actual article categories */}
        <div className="mb-10 flex flex-wrap gap-2" data-testid="article-cat-filter">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`px-4 py-2 rounded-sm border text-sm font-semibold transition-colors ${
                active === c.key
                  ? "bg-navy text-gold border-navy"
                  : "bg-white text-navy border-navy/20 hover:border-gold"
              }`}
              data-testid={`cat-${c.key}`}
            >
              {lang === "ar" ? (c.ar || c.en) : (c.en || c.ar)}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="py-16 text-center text-navy/50 text-sm">
            {lang === "ar" ? "لا توجد مقالات في هذا التصنيف بعد." : "No articles in this category yet."}
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map((a) => {
              const cats = articleCategories(a);
              const catMeta = cats[0] || null;
              const catLabel = catMeta ? (lang === "ar" ? (catMeta.ar || catMeta.en) : (catMeta.en || catMeta.ar)) : null;
              const linkedin = a.linkedin_url && /^https?:\/\//.test(a.linkedin_url) ? a.linkedin_url : null;
              const title = lang === "ar" ? a.title_ar : a.title_en;
              const excerpt = lang === "ar" ? a.excerpt_ar : a.excerpt_en;
              const hasImg = !!a.cover_image;
              return (
                <li key={a.id} data-testid={`article-${a.id}`}
                    className="card-elev overflow-hidden flex flex-col">
                  {hasImg && (
                    <div className="aspect-video bg-cream border-b border-navy/10 overflow-hidden">
                      <img src={a.cover_image} alt=""
                           className="w-full h-full object-cover"
                           data-testid={`article-image-${a.id}`}
                           loading="lazy" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex flex-wrap items-center gap-3 text-sm mb-3">
                      {catLabel && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-widest border"
                              style={{ color: "#8C6A2E", borderColor: "#C9A961", background: "rgba(201,169,97,0.08)" }}
                              data-testid={`article-cat-${a.id}`}>
                          {catLabel}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-navy/70">
                        <Calendar size={13} /> {fmtDate(a.created_at)}
                      </span>
                    </div>
                    <h2 className="text-xl md:text-2xl text-navy leading-snug mb-2">
                      {title}
                    </h2>
                    {excerpt && (
                      <p className="text-navy/75 text-sm md:text-base leading-relaxed line-clamp-3">
                        {excerpt}
                      </p>
                    )}
                    <div className="mt-5 pt-4 border-t border-navy/10">
                      {linkedin ? (
                        <a
                          href={linkedin} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold text-white transition-colors"
                          style={{ background: "#0A66C2" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#084c93")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#0A66C2")}
                          data-testid={`article-linkedin-${a.id}`}
                        >
                          <Linkedin size={16} />
                          {lang === "ar" ? "قراءة على LinkedIn" : "Read on LinkedIn"}
                          <ArrowUpRight size={14} />
                        </a>
                      ) : (
                        <span className="text-xs text-navy/40 italic">
                          {lang === "ar" ? "قريباً — رابط النشر" : "External link coming soon"}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
